import {put, take, select, call, debug, eventChannel, EventChannel} from '../../lib'
import {LOGOUT_PENDING_STORAGE_KEY} from '../../lib/auth/logout_pending'
import {
  AUTH_SET,
  AUTH_REFRESHING_FAILURE,
  AUTH_REFRESH_UNAVAILABLE,
  doAUTH_REFRESHING_FAILURE,
  doSESSION_END_REASON_SET,
  SessionEndReason,
} from '../state'

// `storage` fires in every tab except the one that wrote the key, which is exactly the audience:
// the tab that set the marker has already signed itself out. Only the marker being set (or re-set)
// counts. Its removal is a login or a confirmed logout elsewhere, and `localStorage.clear()`
// arrives with `key: null`. The listener reads the event, never `localStorage` itself, so
// private-mode storage cannot throw here.
const createLogoutPendingChannel = () =>
  eventChannel<true>((emit) => {
    const listener = (event: StorageEvent) => {
      if (event.key === LOGOUT_PENDING_STORAGE_KEY && event.newValue !== null) emit(true)
    }
    window.addEventListener('storage', listener)
    return () => window.removeEventListener('storage', listener)
  })

export function* handleSiblingLogout() {
  debug('saga*handleSiblingLogout')

  // an attempt already in flight passed its marker check before the marker existed, so the
  // AUTH_SET it may still deliver would sign this tab straight back in. Let it land first.
  const isRefreshing: boolean = yield select((state: any) => state?.auth?.isRefreshing)
  if (isRefreshing) yield take([AUTH_SET, AUTH_REFRESHING_FAILURE, AUTH_REFRESH_UNAVAILABLE])

  // no token means the tab is on the login page already, showing a reason of its own
  const accessToken: string | null = yield select((state: any) => state?.auth?.accessToken)
  if (!accessToken) return

  // the refresh coordinator's exit for a pending logout, minus its logout request: the tab that
  // set the marker reloads and retries that logout at boot, so one more per open tab adds nothing
  yield put(doSESSION_END_REASON_SET({reason: SessionEndReason.SessionEnded, at: Date.now()}))
  yield put(doAUTH_REFRESHING_FAILURE())
}

export function* handleSiblingLogoutSaga() {
  const channel: EventChannel<true> = yield call(createLogoutPendingChannel)
  for (;;) {
    yield take(channel)
    yield call(handleSiblingLogout)
  }
}
